/**
 * @desc 沙箱辅助方法
 * @time 2023-07-31 16:08:38
 * @user simxin
 */

import { unmountComponentAtNode } from 'react-dom';

/**
 * @desc run script file, get exports object
 */
export async function parseUmdExports(urls: string[], proxy: Window) {
  let contents = await fetchSourcesContent(urls);

  // unique webpackJsonp_
  const finalName = `webpackJsonp_${new Date().getTime()}`;
  contents = contents.map((content: string) => {
    content.replace(/webpackJsonp[A-Za-z0-9_-]+/g, finalName);
    return content;
  });

  for (let i = 0; i < urls.length; i++) {
    const exports = execScriptCode(urls[i], contents[i], proxy);
    if (i === urls.length - 1) {
      return exports;
    }
  }
}

/**
 * @desc fetch file content
 */
const sourceCache: { [index: string]: string } = {};
export function fetchSourcesContent(urls: string[]): Promise<string[]> {
  const fetchContent = async (url: string) => {
    if (sourceCache[url]) {
      return sourceCache[url];
    }
    const resp: Response = await fetch(url);
    sourceCache[url] = await resp.text();
    return sourceCache[url];
  };
  return Promise.all(urls.map(url => fetchContent(url)));
}

/**
 * @desc exec script file and return export object
 */
async function execScriptCode(url: string, content: string, proxy: Window) {
  const prev = markProxyProp(proxy);
  globalEval(proxy, url, content);
  const exports = proxy[diffProxyProp(proxy, prev) as any] || {};
  return exports;
}

/**
 * @desc eval script in global window
 */
function globalEval(proxy: Window, url: string, content: string) {
  const code = packExecutableScript(url, content, proxy);
  (0, eval)(code);
}

/**
 * @desc pack script content with proxy
 */
function packExecutableScript(
  url: string,
  content: string,
  proxy: Window,
): string {
  const comment = `//# source=${url}\n`;
  const gwindow = (0, eval)('window');
  gwindow.proxy = proxy;
  return `;(function(window){;${content}\n${comment}}).bind(window.proxy)(window.proxy);`;
}

/**
 * @desc mark prev props
 */
function markProxyProp(proxy: Window): string[] {
  return Object.keys(proxy).map((p: string) => p);
}

/**
 * @desc diff with prev prop
 */
function diffProxyProp(proxy: Window, prev: string[]): string {
  return Object.keys(proxy).reduce(
    (s: string, c: string) => (!prev.includes(c) ? c : s),
    '',
  );
}

/**
 * @desc load app module, include dom、style and script
 */
export async function loadModule(
  id: string,
  scripts: string[],
  styles: string[],
) {
  await Promise.all([
    ...scripts.map(url => catchFn(retryFn(loadScript, 3, 1000))(id, url)),
    ...styles.map(url => catchFn(retryFn(loadStyle, 3, 1000))(id, url)),
  ]);
}

/**
 * @desc unload app module, include dom、style and script
 */
export function unloadModule(id: string): void {
  unloadScript(id);
  unloadStyle(id);
  unloadDom(id);
}

/**
 * @desc load script element and set data-res-id
 */
export async function loadScript(id: string, src: string): Promise<unknown> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    // script.crossOrigin = 'use-credentials';
    script.dataset.resId = id;
    script.onload = () => resolve(null);
    script.onerror = e => reject([e, src]);
    document.body.appendChild(script);
  });
}

/**
 * @desc load style element and set data-res-id
 */
export async function loadStyle(id: string, url: string): Promise<unknown> {
  const [content] = await fetchSourcesContent([url]);
  return new Promise((resolve, reject) => {
    const style = document.createElement('style');
    style.textContent = content;
    style.dataset.resId = id;
    style.dataset.url = url;
    style.onload = () => resolve(null);
    style.onerror = e => reject([e, url]);
    document.body.appendChild(style);
  });
}

/**
 * @desc load link element and set data-res-id
 */
export function loadLink(
  id: string,
  url: string,
  cb?: (...arg: any) => void,
): void {
  // unloadLink(id);
  const node = document.createElement('link');
  node.dataset.resId = id;
  node.rel = 'stylesheet';
  node.href = url;
  cb && (node.onload = () => cb(true));
  cb && (node.onerror = () => cb(false));
  document.head.appendChild(node);
}

/**
 * @desc unload dom element with domId
 */
export function unloadDom(id: string) {
  const dom = document.getElementById(id);
  if (dom) {
    unmountComponentAtNode(document.getElementById(id) as Element);
    dom.innerHTML = '';
  }
}

/**
 * @desc unload script element with data-res-id
 */
export function unloadScript(id: string) {
  const scripts = document.querySelectorAll(`script[data-res-id="${id}"]`);
  if (scripts) {
    for (const script of Array.from(scripts)) {
      if (script.parentElement) {
        script.parentElement.removeChild(script);
      }
    }
  }
}

/**
 * @desc unload style element with data-res-id
 */
export function unloadStyle(id: string) {
  const styles = document.querySelectorAll(`link[data-res-id="${id}"]`);
  if (styles) {
    for (const style of Array.from(styles)) {
      if (style.parentElement) {
        style.parentElement.removeChild(style);
      }
    }
  }
}

/**
 * @desc unload link element with data-res-id
 */
export function unloadLink(id: string) {
  const links = document.querySelectorAll(`link[data-res-id="${id}"]`);
  if (links) {
    for (const link of Array.from(links)) {
      if (link.parentElement) {
        link.parentElement.removeChild(link);
      }
    }
  }
}

/**
 * @desc catch when fn exec error
 */
export function catchFn<T>(fn: (...arg: T[]) => Promise<unknown>) {
  return (...arg: T[]) => new Promise((resolve) => {
    fn(...arg)
      .then(resolve)
      .catch(() => resolve(false));
  });
}

/**
 * @desc retry when fn exec error
 */
export function retryFn<T>(
  fn: (...arg: T[]) => Promise<unknown>,
  times: number,
  delay: number,
) {
  return (...arg: T[]) => new Promise((resolve, reject) => {
    const attempt = () => {
      fn(...arg)
        .then(resolve)
        .catch((e) => {
          if (times === 1) return reject(e);
          times = times - 1;
          setTimeout(attempt, delay);
        });
    };
    attempt();
  });
}
